import {
  type InstallmentPlanRequest,
  installmentPlanRequestSchema,
  MAX_INSTALLMENTS,
} from '@familywallet/shared';
import { z } from 'zod';
import { parsePositive } from './add-form';

/** What the installment plan form holds, as typed. */
export interface PlanFormValues {
  amount: string;
  dayOfMonth: string;
  /** YYYY-MM (an `<input type="month">`). */
  startMonth: string;
  mode: 'count' | 'untilPaid';
  count: string;
}

export interface PlanFormContext {
  currency: string;
  /** Changing an existing plan: the start month is only sent when it was moved. */
  defaultStartMonth: string | null;
}

interface Issue {
  path: string[];
  message: string;
}

const wholeNumber = (raw: string, min: number, max: number): number | null => {
  if (!/^\d{1,3}$/.test(raw.trim())) return null;
  const value = Number.parseInt(raw.trim(), 10);
  return value >= min && value <= max ? value : null;
};

export function checkPlanForm(v: PlanFormValues, ctx: PlanFormContext): Issue[] {
  const issues: Issue[] = [];
  if (!parsePositive(v.amount, ctx.currency)) {
    issues.push({
      path: ['amount'],
      message:
        v.amount.trim() === ''
          ? 'add.errors.amountRequired'
          : ctx.currency === 'LBP' && parsePositive(v.amount, 'USD')
            ? 'add.errors.amountWhole'
            : 'add.errors.amount',
    });
  }
  if (wholeNumber(v.dayOfMonth, 1, 31) === null) {
    issues.push({ path: ['dayOfMonth'], message: 'debts.errors.day' });
  }
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(v.startMonth)) {
    issues.push({ path: ['startMonth'], message: 'debts.errors.month' });
  }
  if (v.mode === 'count' && wholeNumber(v.count, 1, MAX_INSTALLMENTS) === null) {
    issues.push({ path: ['count'], message: 'debts.errors.count' });
  }
  return issues;
}

/** The request for PUT /debts/:id/plan, checked by the shared schema the API uses. */
export function toPlanRequest(v: PlanFormValues, ctx: PlanFormContext): InstallmentPlanRequest {
  const moved = ctx.defaultStartMonth === null || v.startMonth !== ctx.defaultStartMonth;
  return installmentPlanRequestSchema.parse({
    amount: parsePositive(v.amount, ctx.currency)!,
    dayOfMonth: wholeNumber(v.dayOfMonth, 1, 31)!,
    ...(moved ? { startMonth: v.startMonth } : {}),
    count: v.mode === 'count' ? wholeNumber(v.count, 1, MAX_INSTALLMENTS)! : null,
  }) as InstallmentPlanRequest;
}

export function planFormSchema(ctx: PlanFormContext) {
  return z
    .custom<PlanFormValues>()
    .superRefine((v, c) => {
      for (const issue of checkPlanForm(v, ctx)) {
        c.addIssue({ code: 'custom', path: issue.path, message: issue.message });
      }
    })
    .transform((v) => toPlanRequest(v, ctx));
}
